import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DeviceMobileIcon, XIcon, CaretRightIcon } from '@phosphor-icons/react';

const DISMISS_STORAGE_KEY = 'pwa-install-dismissed';

/**
 * Detects if the app is running as an installed PWA
 */
function isInstalledPWA(): boolean {
  if (typeof window === 'undefined') return false;

  // Check display-mode media query (most reliable)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // Check iOS standalone mode
  const isIOSStandalone =
    'standalone' in window.navigator &&
    (window.navigator as Navigator & { standalone?: boolean }).standalone ===
      true;

  return isStandalone || isIOSStandalone;
}

/**
 * Detects iOS platform for customized messaging
 */
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

/**
 * InstallCard component displays a card prompting users to install the PWA.
 * The card automatically hides if:
 * - The app is already installed
 * - The user has dismissed it permanently
 *
 * On click, navigates to the installation guide page with detailed instructions.
 */
export function InstallCard() {
  // Initialize states synchronously from available data
  const [isDismissed, setIsDismissed] = useState(
    () => localStorage.getItem(DISMISS_STORAGE_KEY) === 'true'
  );

  // Check installation status
  const isInstalled = isInstalledPWA();

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem(DISMISS_STORAGE_KEY, 'true');
    setIsDismissed(true);
  };

  // Don't show card if installed or dismissed
  if (isInstalled || isDismissed) {
    return null;
  }

  return (
    <Link to="/install-guide" className="block">
      <Card className="bg-slate-600 mb-6 cursor-pointer group">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex justify-between">
                <div className="flex gap-2 items-center mb-2">
                  <DeviceMobileIcon
                    size={24}
                    weight="bold"
                    className="text-purple-300"
                  />
                  <span className="text-large">
                    Get the full app experience
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleDismiss}
                  className="text-slate-400 hover:text-foreground bg-slate-700 rounded-full"
                  aria-label="Dismiss install prompt"
                >
                  <XIcon size={16} weight="bold" />
                </Button>
              </div>
              <p className="text-body text-slate-400">
                {isIOS()
                  ? 'Add RISE to your home screen for the full experience.'
                  : 'Install RISE to your device for the full experience.'}
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="inline-flex items-center gap-1 h-9 rounded-md px-3 bg-purple-300 text-slate-600 text-body group-hover:bg-purple-400 transition-colors">
                  {isIOS() ? 'How to Add' : 'How to Install'}
                  <CaretRightIcon size={14} weight="bold" />
                </span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

